import { useState } from 'react';
import styled from '@emotion/styled';
import { AnimatePresence, m } from 'framer-motion';
import { Interweave } from 'interweave';
import useSWR from 'swr';

import { screen } from '@/components/common/breakpoints';
import { PrimaryButton } from '@/components/common/Buttons';
import { Icon } from '@/components/common/Icon';
import {
  Heading,
  HeadingLevel,
  List,
  Paragraph,
} from '@/components/common/MarkUp';
import { skeleton } from '@/components/common/skeleton';
import { useJobListContext } from '@/components/page/careers/context/JobListContext';
import { JobSubTitle } from '@/components/page/careers/JobSubTitle';
import { colourTokens } from '@/styles/colours';
import {
  bodyMediumSemiBold,
  captionSmall,
  headlineSmall,
  subheadlineBold,
} from '@/styles/typography';

import type { AtsJobPosting, AtsJobPostingInfo } from '@/lib/AtsApi/types';
import type { ReactNode } from 'react';

interface JobPostingProps extends AtsJobPosting {}

const fetcher = (input: RequestInfo, init: RequestInit) =>
  fetch(input, init).then((res) => res.json());

const JobPosting = ({
  title,
  id,
  externalLink,
  locationIds,
}: JobPostingProps) => {
  const { atsData } = useJobListContext();
  const [isOpen, setIsOpen] = useState(false);

  const locationData = atsData.locations.find(
    (location) => location.id === locationIds.primaryLocationId,
  );

  const { data } = useSWR<AtsJobPostingInfo, any>(
    isOpen ? `/api/jobs/${id}` : null,
    fetcher,
  );

  const normalisedJobTitle = title.replace(/[\s:,]/g, '-');

  return (
    <JobContainer>
      <HeadingLevel>
        <HeadingLevel>
          <Heading>
            <JobHeader
              type="button"
              isOpen={isOpen}
              onClick={() => setIsOpen(!isOpen)}
              aria-expanded={isOpen}
              aria-controls={`${normalisedJobTitle}-expanded-content`}
              id={id}
            >
              <JobHeaderDetails>
                <JobLocation>
                  <JobSubTitle
                    locationName={locationData.name}
                    locationCountry={
                      locationData.address.postalAddress.addressCountry
                    }
                    isRemote={locationData.isRemote}
                  />
                </JobLocation>
                <JobTitle> {title}</JobTitle>
              </JobHeaderDetails>
              <ChevronWrapper isOpen={isOpen}>
                <Icon
                  icon="ChevronDown"
                  colour={isOpen ? '--icon-action' : '--icon-inverse-strong'}
                  size="15px"
                />
              </ChevronWrapper>
            </JobHeader>
          </Heading>
        </HeadingLevel>
        <AnimatePresence initial={false} key={id}>
          {isOpen && (
            <ExpandedContent
              layout
              initial="collapsed"
              animate="open"
              exit="collapsed"
              variants={additionalContentVariant}
              transition={additionalContentTransition}
              id={`${normalisedJobTitle}-expanded-content`}
              aria-labelledby={id}
              role="region"
              data-testid={`${normalisedJobTitle}-expanded-content`}
            >
              <ContentDescription isOpen={isOpen}>
                {data && (
                  <>
                    <Interweave
                      content={data.descriptionHtml}
                      containerTagName="fragment"
                      transform={transform}
                    />
                    <PrimaryButton
                      componentTheme="light"
                      href={externalLink}
                      className="apply-button"
                    >
                      Apply
                    </PrimaryButton>
                  </>
                )}
              </ContentDescription>
            </ExpandedContent>
          )}
        </AnimatePresence>
      </HeadingLevel>
    </JobContainer>
  );
};

function transform(node: HTMLElement, children: ReactNode[]): ReactNode {
  if (node.tagName === 'P') {
    return <Paragraph size="body-md">{children}</Paragraph>;
  }
  if (node.tagName === 'STRONG') {
    return <b>{children}</b>;
  }
  if (node.tagName === 'EM') {
    return <em>{children}</em>;
  }
  if (node.tagName === 'UL') {
    return <List>{children}</List>;
  }

  if (node.tagName === 'LI') {
    return (
      <li>
        <StyledIcon icon="Hexagon" /> {children}
      </li>
    );
  }

  if (node.tagName === 'A') {
    return (
      <a
        href={(node as HTMLAnchorElement).href}
        rel="nofollow noreferrer noopener"
        target="_blank"
      >
        {children}
      </a>
    );
  }

  return null;
}

const JobContainer = styled.div`
  background-color: var(${colourTokens.surface.subtle.token});
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: var(--spacing-small) var(--spacing-small) var(--spacing-small)
    var(--spacing-small);
  border-radius: var(--radius-m);
  overflow: hidden;
  margin-top: var(--spacing-medium);
  &:focus-visible {
    outline: 2px solid var(--colour-ultraviolet);
  }

  ${screen.md} {
    padding: var(--spacing-medium);
  }
`;

const ExpandedContent = styled(m.div)`
  will-change: height, opacity;
`;

const ContentDescription = styled.div<{ isOpen: boolean }>`
  margin-top: var(--spacing-medium);

  ${screen.sm} {
    padding: 0 var(--spacing-small);
  }

  p {
    margin: var(--spacing-small) 0 0 0;
    &:has(b) {
      margin-top: var(--spacing-large);
    }
    b {
      ${bodyMediumSemiBold};
    }
  }
  ul {
    margin: var(--spacing-small) 0;
    padding: 0 var(--spacing-small);
    list-style: initial;
    li {
      margin-left: var(--spacing-xxx-small);
      margin-bottom: var(--spacing-xx-small);
      ::marker {
        font-size: 24px;
      }
      list-style-position: outside;
      svg {
        display: none;
      }
      p {
        margin-top: 0;
      }
    }
    &:last-of-type {
      background: var(--surface-primary-subtle);
      padding: var(--spacing-300) var(--spacing-200);
      border-radius: var(--radius-m);
      li {
        list-style: none;
        display: flex;
        flex-direction: row;
        align-items: baseline;
        justify-content: start;
        margin-left: 0;
        svg {
          display: initial;
          margin-right: var(--spacing-xx-small);
          min-width: 20px;
        }
      }

      ${screen.sm} {
        padding-left: var(--spacing-300);
        padding-right: var(--spacing-300);
      }
    }
  }

  &:empty {
    ${(props) =>
      props.isOpen
        ? skeleton(
            [
              [450, 20],
              [540, 20],
              [540, 20],
              [540, 20],
            ],
            200,
          )
        : ''};
  }

  .apply-button {
    margin-top: var(--spacing-small);
  }
`;

const JobHeader = styled.button<{ isOpen: boolean }>`
  ${headlineSmall}
  font-weight: var(--font-weight-semi-bold);
  color: var(--text-strong);
  display: flex;
  gap: var(--spacing-x-small);
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  appearance: none;
  border: none;
  background: none;
  text-align: start;
  padding: 0;
  width: 100%;
  &:focus-visible {
    outline: 2px solid var(--colour-ultraviolet);
    outline-offset: 6px;
  }

  ${screen.md} {
    align-items: center;
  }
`;

const JobHeaderDetails = styled.span`
  display: flex;
  flex-direction: column;
`;

const JobLocation = styled.span`
  ${captionSmall}
  color: var(--text-action);
  margin-bottom: var(--spacing-xx-small);
`;

const JobTitle = styled.span`
  ${subheadlineBold}
  color: var(--text-strong);
`;

const ChevronWrapper = styled.div<{ isOpen: boolean }>`
  border: 1.5px solid var(--icon-action);
  border-radius: 50%;
  padding: var(--spacing-150);
  transition: background-color 0.6s;
  background-color: ${({ isOpen }) =>
    isOpen ? 'transparent' : 'var(--icon-action)'};

  svg {
    transition: transform 0.6s;
    transform: ${({ isOpen }) => (isOpen ? 'rotate(180deg)' : 'none')};
  }
`;

// Animation Props
const additionalContentTransition = {
  height: { duration: 0.4 },
  opacity: { delay: 0.3, duration: 0.6 },
  delayChildren: 0.2,
  staggerChildren: 0.2,
};

const additionalContentVariant = {
  open: {
    opacity: 1,
    height: 'auto',
  },
  collapsed: {
    opacity: 0,
    height: 0,
    transition: {
      opacity: { delay: 0, duration: 0.1 },
      height: { delay: 0.2 },
    },
  },
};

const StyledIcon = styled(Icon)`
  left: -10px;
  position: relative;
  top: 5px;
`;

export { JobPosting };
